/**
 * /payroll/employees/:employeeId — Employee Detail
 * Tabs: Profile | W-4 & Tax | Bank Accounts | Onboarding & I-9
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { MoneyField } from "@/components/MoneyField";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BlockIcon from "@mui/icons-material/Block";
import AddIcon from "@mui/icons-material/Add";
import DownloadIcon from "@mui/icons-material/Download";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SendIcon from "@mui/icons-material/Send";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import { api } from "@/lib/api";
import { payrollEmployeeQueryOptions, payrollKeys } from "@/lib/queries";
import type { PayrollEmployee } from "@/lib/types";
import { HelpHint } from "@/components/HelpHint";

export const Route = createFileRoute("/_auth/payroll/employees_/$employeeId/")({
  component: EmployeeDetailPage,
});

const FREQ_LABEL: Record<string, string> = {
  weekly: "Weekly",
  biweekly: "Bi-weekly",
  semi_monthly: "Semi-monthly",
  monthly: "Monthly",
};
const FILING_STATUS_OPTIONS = [
  { value: "single", label: "Single" },
  { value: "married_filing_jointly", label: "Married Filing Jointly" },
  { value: "head_of_household", label: "Head of Household" },
];
const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD",
  "MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC",
  "SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC",
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

interface W4Current {
  tax_setup_id: string;
  work_state: string;
  effective_start: string;
  federal_w4: {
    filing_status: string;
    multiple_jobs?: boolean;
    dependents_amt?: string;
    other_income?: string;
    deductions?: string;
    extra_withholding?: string;
    exempt?: boolean;
  };
}

interface W4SubmitResult {
  tax_setup_id: string;
  document_id: string;
  document_url: string;
  effective_date: string;
}

interface OnboardingTokenResult {
  token_id: string;
  raw_token: string;
  expires_at: string;
  pending_steps: string[];
}

interface I9Out {
  id: string;
  status: string;
  method: string;
  section1_completed_at: string | null;
  section2_completed_at: string | null;
  file_url: string | null;
}

// ─── Profile tab ─────────────────────────────────────────────────────────────

function ProfileTab({ employee }: { employee: PayrollEmployee }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    first_name: employee.first_name,
    last_name: employee.last_name,
    pay_frequency: employee.pay_frequency,
    pay_type: employee.pay_type,
    default_rate: employee.default_rate,
    address_city: employee.address_city ?? "",
    address_state: employee.address_state ?? "",
  });
  const [terminateOpen, setTerminateOpen] = useState(false);
  const [termDate, setTermDate] = useState(new Date().toISOString().split("T")[0]);

  const updateMut = useMutation({
    mutationFn: (body: Partial<typeof form>) =>
      api<PayrollEmployee>(`/api/payroll/employees/${employee.id}`, { method: "PATCH", body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: payrollKeys.employee(employee.id) });
      setEditing(false);
    },
  });

  const terminateMut = useMutation({
    mutationFn: () =>
      api(`/api/payroll/employees/${employee.id}/terminate?term_date=${termDate}`, {
        method: "POST",
        body: {},
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: payrollKeys.employee(employee.id) });
      setTerminateOpen(false);
    },
  });

  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | { value: unknown }>) =>
    setForm(f => ({ ...f, [k]: (e.target as HTMLInputElement).value }));

  return (
    <Box sx={{ maxWidth: 560, display: "flex", flexDirection: "column", gap: 3 }}>
      {!employee.is_active && (
        <Alert severity="warning">
          This employee was terminated on {employee.term_date}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="subtitle1" fontWeight={600}>Personal Details</Typography>
          {!editing ? (
            <Button size="small" onClick={() => setEditing(true)}>Edit</Button>
          ) : (
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button size="small" onClick={() => setEditing(false)}>Cancel</Button>
              <Button
                size="small" variant="contained"
                onClick={() => updateMut.mutate(form)}
                disabled={updateMut.isPending}
              >
                Save
              </Button>
            </Box>
          )}
        </Box>

        {editing ? (
          <>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
              <TextField label="First Name" value={form.first_name} onChange={set("first_name")} size="small" />
              <TextField label="Last Name" value={form.last_name} onChange={set("last_name")} size="small" />
            </Box>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
              <FormControl size="small">
                <InputLabel>Pay Type</InputLabel>
                <Select value={form.pay_type} label="Pay Type" onChange={(e) => setForm(f => ({ ...f, pay_type: e.target.value }))}>
                  <MenuItem value="hourly">Hourly</MenuItem>
                  <MenuItem value="salary">Salary</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small">
                <InputLabel>Frequency</InputLabel>
                <Select value={form.pay_frequency} label="Frequency" onChange={(e) => setForm(f => ({ ...f, pay_frequency: e.target.value }))}>
                  <MenuItem value="weekly">Weekly</MenuItem>
                  <MenuItem value="biweekly">Bi-weekly</MenuItem>
                  <MenuItem value="semi_monthly">Semi-monthly</MenuItem>
                  <MenuItem value="monthly">Monthly</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <MoneyField
              label={form.pay_type === "hourly" ? "Hourly rate" : "Annual salary"}
              value={form.default_rate}
              onChange={set("default_rate")}
              size="small"
            />
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
              <TextField label="City" value={form.address_city} onChange={set("address_city")} size="small" />
              <TextField
                label="State" value={form.address_state} onChange={set("address_state")}
                size="small" inputProps={{ maxLength: 2 }}
              />
            </Box>
          </>
        ) : (
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
            {[
              ["First Name", employee.first_name],
              ["Last Name", employee.last_name],
              ["Pay Type", employee.pay_type],
              ["Frequency", FREQ_LABEL[employee.pay_frequency] ?? employee.pay_frequency],
              [
                "Rate",
                employee.pay_type === "hourly"
                  ? `$${parseFloat(employee.default_rate).toFixed(2)}/hr`
                  : `$${parseFloat(employee.default_rate).toLocaleString()}/yr`,
              ],
              [
                "Location",
                [employee.address_city, employee.address_state].filter(Boolean).join(", ") || "—",
              ],
            ].map(([label, val]) => (
              <Box key={label}>
                <Typography variant="caption" color="text.secondary">{label}</Typography>
                <Typography variant="body2">{val}</Typography>
              </Box>
            ))}
          </Box>
        )}
      </Paper>

      {employee.is_active && (
        <Button
          color="error" variant="outlined" startIcon={<BlockIcon />}
          onClick={() => setTerminateOpen(true)}
          sx={{ alignSelf: "flex-start" }}
        >
          Terminate Employee
        </Button>
      )}

      <Dialog open={terminateOpen} onClose={() => setTerminateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Terminate Employee</DialogTitle>
        <DialogContent sx={{ mt: 1 }}>
          <TextField
            label="Termination Date" type="date" value={termDate}
            onChange={(e) => setTermDate(e.target.value)}
            InputLabelProps={{ shrink: true }} fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTerminateOpen(false)}>Cancel</Button>
          <Button
            color="error" variant="contained"
            onClick={() => terminateMut.mutate()} disabled={terminateMut.isPending}
          >
            {terminateMut.isPending ? "Terminating…" : "Terminate"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ─── W-4 & Tax tab ────────────────────────────────────────────────────────────

function W4TaxTab({ employee }: { employee: PayrollEmployee }) {
  const qc = useQueryClient();

  // Load current W-4
  const { data: currentW4, isLoading: w4Loading } = useQuery<W4Current>({
    queryKey: ["payroll", "employees", employee.id, "w4"],
    queryFn: () => api<W4Current>(`/api/payroll/employees/${employee.id}/w4`),
    retry: false,
  });

  const [form, setForm] = useState({
    filing_status: "single",
    multiple_jobs_checked: false,
    dependents_amount: "0",
    other_income: "0",
    deductions: "0",
    extra_withholding: "0",
    exempt: false,
    work_state: employee.address_state ?? "",
    employer_name: "",
    employer_ein: "",
    effective_date: new Date().toISOString().split("T")[0],
  });

  const [submitted, setSubmitted] = useState<W4SubmitResult | null>(null);

  const submitMut = useMutation({
    mutationFn: () =>
      api<W4SubmitResult>(`/api/payroll/employees/${employee.id}/w4`, {
        method: "POST",
        body: {
          filing_status: form.filing_status,
          multiple_jobs_checked: form.multiple_jobs_checked,
          dependents_amount: parseFloat(form.dependents_amount) || 0,
          other_income: parseFloat(form.other_income) || 0,
          deductions: parseFloat(form.deductions) || 0,
          extra_withholding: parseFloat(form.extra_withholding) || 0,
          exempt: form.exempt,
          work_state: form.work_state,
          employer_name: form.employer_name,
          employer_ein: form.employer_ein,
          effective_date: form.effective_date,
        },
      }),
    onSuccess: (data) => {
      setSubmitted(data);
      qc.invalidateQueries({ queryKey: ["payroll", "employees", employee.id, "w4"] });
    },
  });

  const FILING_STATUS_LABEL: Record<string, string> = {
    single: "Single / Married filing separately",
    married_filing_jointly: "Married filing jointly",
    head_of_household: "Head of household",
  };

  return (
    <Box sx={{ maxWidth: 600, display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Current W-4 summary */}
      {w4Loading ? (
        <CircularProgress size={20} />
      ) : currentW4 ? (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <AssignmentTurnedInIcon color="success" fontSize="small" />
            <Typography variant="subtitle2" fontWeight={600} color="success.main">
              W-4 on file — effective {currentW4.effective_start}
            </Typography>
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 1.5 }}>
            {[
              ["Filing Status", FILING_STATUS_LABEL[currentW4.federal_w4.filing_status] ?? currentW4.federal_w4.filing_status],
              ["Work State", currentW4.work_state],
              ["Extra W/H", `$${currentW4.federal_w4.extra_withholding ?? "0"}/period`],
            ].map(([label, val]) => (
              <Box key={label}>
                <Typography variant="caption" color="text.secondary">{label}</Typography>
                <Typography variant="body2" fontWeight={500}>{val}</Typography>
              </Box>
            ))}
          </Box>
        </Paper>
      ) : (
        <Alert severity="info">No W-4 on file. Submit one below to enable tax withholding calculations.</Alert>
      )}

      {/* Submit new W-4 */}
      <Paper variant="outlined" sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          {currentW4 ? "Update W-4" : "Submit W-4"} — Federal Form W-4 (2020+)
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Each submission creates a new versioned record. Prior W-4s are preserved for audit per IRS Rev. Proc. 2017-72.
        </Typography>

        {/* Step 1 */}
        <FormControl size="small">
          <InputLabel>Step 1c — Filing Status</InputLabel>
          <Select
            value={form.filing_status}
            label="Step 1c — Filing Status"
            onChange={(e) => setForm(f => ({ ...f, filing_status: e.target.value }))}
          >
            {FILING_STATUS_OPTIONS.map(o => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Step 2 */}
        <FormControlLabel
          control={
            <Switch
              checked={form.multiple_jobs_checked}
              onChange={(e) => setForm(f => ({ ...f, multiple_jobs_checked: e.target.checked }))}
              size="small"
            />
          }
          label="Step 2 — Multiple jobs or spouse also works (check box 2c)"
        />

        {/* Step 3 */}
        <MoneyField
          label="Step 3 — Qualifying children + other dependents / yr"
          size="small"
          fullWidth
          value={form.dependents_amount}
          onChange={(v) => setForm((f) => ({ ...f, dependents_amount: v }))}
          helperText="From W-4 line 3 — total child tax credit + other dependent credits for the year"
        />

        {/* Step 4 */}
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
          <MoneyField
            label="Step 4a — Other income / yr"
            size="small"
            value={form.other_income}
            onChange={(v) => setForm((f) => ({ ...f, other_income: v }))}
          />
          <MoneyField
            label="Step 4b — Deductions / yr"
            size="small"
            value={form.deductions}
            onChange={(v) => setForm((f) => ({ ...f, deductions: v }))}
          />
        </Box>
        <MoneyField
          label="Step 4c — Extra withholding / pay period"
          size="small"
          fullWidth
          value={form.extra_withholding}
          onChange={(v) => setForm((f) => ({ ...f, extra_withholding: v }))}
        />

        <FormControlLabel
          control={
            <Switch
              checked={form.exempt}
              onChange={(e) => setForm(f => ({ ...f, exempt: e.target.checked }))}
              size="small"
            />
          }
          label="Exempt from federal withholding (line 4 — rarely applicable)"
        />

        <Divider />

        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
          <FormControl size="small">
            <InputLabel>Primary Work State</InputLabel>
            <Select
              value={form.work_state}
              label="Primary Work State"
              onChange={(e) => setForm(f => ({ ...f, work_state: e.target.value }))}
            >
              {US_STATES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField
            label="Effective Date" type="date" size="small"
            value={form.effective_date}
            onChange={(e) => setForm(f => ({ ...f, effective_date: e.target.value }))}
            InputLabelProps={{ shrink: true }}
          />
        </Box>

        <Divider />
        <Typography variant="caption" color="text.secondary">Employer info — printed on the generated PDF</Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 2 }}>
          <TextField
            label="Employer Name" size="small" value={form.employer_name}
            onChange={(e) => setForm(f => ({ ...f, employer_name: e.target.value }))}
          />
          <TextField
            label="EIN (XX-XXXXXXX)" size="small" value={form.employer_ein}
            onChange={(e) => setForm(f => ({ ...f, employer_ein: e.target.value }))}
            inputProps={{ maxLength: 11 }}
          />
        </Box>

        {submitMut.isError && (
          <Alert severity="error">{String((submitMut.error as Error)?.message)}</Alert>
        )}

        {submitted && (
          <Alert
            severity="success"
            action={
              <Button
                size="small" color="inherit" startIcon={<DownloadIcon />}
                href={submitted.document_url} target="_blank" rel="noopener"
              >
                Download PDF
              </Button>
            }
          >
            W-4 saved — effective {submitted.effective_date}. PDF generated and stored for audit retention.
          </Alert>
        )}

        <Button
          variant="contained"
          onClick={() => submitMut.mutate()}
          disabled={submitMut.isPending}
          sx={{ alignSelf: "flex-start" }}
        >
          {submitMut.isPending ? "Generating…" : "Submit W-4 + Generate PDF"}
        </Button>
      </Paper>
    </Box>
  );
}

// ─── Bank Accounts tab ────────────────────────────────────────────────────────

function BankAccountsTab({ employee }: { employee: PayrollEmployee }) {
  const qc = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({
    routing_number: "",
    account_number_encrypted: "",
    account_type: "checking",
    is_primary: true,
    account_nickname: "",
  });

  const mut = useMutation({
    mutationFn: (body: typeof form) =>
      api(`/api/payroll/employees/${employee.id}/bank-accounts`, { method: "POST", body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: payrollKeys.employee(employee.id) });
      setAddOpen(false);
      setForm({
        routing_number: "",
        account_number_encrypted: "",
        account_type: "checking",
        is_primary: true,
        account_nickname: "",
      });
    },
  });

  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | { value: unknown }>) =>
    setForm(f => ({ ...f, [k]: (e.target as HTMLInputElement).value }));

  return (
    <Box sx={{ maxWidth: 560 }}>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>
          Add Bank Account
        </Button>
      </Box>

      <Paper variant="outlined" sx={{ p: 3 }}>
        <Typography color="text.secondary" variant="body2">
          Bank accounts are used for ACH direct deposit. Add at least one account to enable direct deposit.
          Routing and account numbers are stored encrypted at rest.
        </Typography>
      </Paper>

      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Direct Deposit Account</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          <TextField
            label="Account Nickname (optional)" value={form.account_nickname}
            onChange={set("account_nickname")} size="small"
          />
          <TextField
            label="Routing Number (9 digits)" value={form.routing_number}
            onChange={set("routing_number")} required size="small" inputProps={{ maxLength: 9 }}
          />
          <TextField
            label="Account Number" value={form.account_number_encrypted}
            onChange={set("account_number_encrypted")} required size="small"
            helperText="Stored encrypted at rest"
          />
          <FormControl size="small">
            <InputLabel>Account Type</InputLabel>
            <Select
              value={form.account_type} label="Account Type"
              onChange={(e) => setForm(f => ({ ...f, account_type: e.target.value }))}
            >
              <MenuItem value="checking">Checking</MenuItem>
              <MenuItem value="savings">Savings</MenuItem>
            </Select>
          </FormControl>
          {mut.isError && (
            <Alert severity="error">{String((mut.error as Error)?.message)}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => mut.mutate(form)}
            disabled={mut.isPending || !form.routing_number || !form.account_number_encrypted}
          >
            {mut.isPending ? "Saving…" : "Add Account"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ─── Onboarding & I-9 tab ────────────────────────────────────────────────────

function OnboardingTab({ employee }: { employee: PayrollEmployee }) {
  const [tokenResult, setTokenResult] = useState<OnboardingTokenResult | null>(null);
  const [copySnack, setCopySnack] = useState(false);
  const [i9FileUrl, setI9FileUrl] = useState("");
  const [i9Result, setI9Result] = useState<I9Out | null>(null);

  const tokenMut = useMutation({
    mutationFn: () =>
      api<OnboardingTokenResult>(
        `/api/payroll/employees/${employee.id}/onboarding-token`,
        { method: "POST", body: {} },
      ),
    onSuccess: (data) => setTokenResult(data),
  });

  const i9Mut = useMutation({
    mutationFn: () =>
      api<I9Out>(
        `/api/payroll/employees/${employee.id}/i9/complete-section2`,
        { method: "POST", body: { file_url: i9FileUrl || null } },
      ),
    onSuccess: (data) => setI9Result(data),
  });

  const copyToken = () => {
    if (tokenResult) {
      navigator.clipboard.writeText(tokenResult.raw_token);
      setCopySnack(true);
    }
  };

  const onboardingLink = tokenResult
    ? `${window.location.origin}/onboarding?token=${tokenResult.raw_token}`
    : null;

  return (
    <Box sx={{ maxWidth: 600, display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Onboarding token */}
      <Paper variant="outlined" sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2 }}>
        <Box>
          <Typography variant="subtitle1" fontWeight={600}>Employee Self-Service Onboarding Link</Typography>
          <Typography variant="caption" color="text.secondary">
            Generates a secure, single-use link the employee uses to submit their own W-4, state withholding,
            and bank account. Valid 72 hours. The raw token is shown once — store it before closing or
            generate a new one.
          </Typography>
        </Box>

        {tokenResult ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Alert severity="success" icon={<SendIcon />}>
              Onboarding link generated. Send this to the employee:
            </Alert>
            <Paper
              variant="outlined"
              sx={{ p: 1.5, bgcolor: "action.hover", fontFamily: "monospace", fontSize: "0.78rem", wordBreak: "break-all" }}
            >
              {onboardingLink}
            </Paper>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                size="small" startIcon={<ContentCopyIcon />}
                variant="outlined" onClick={copyToken}
              >
                Copy Link
              </Button>
              <Button
                size="small" variant="text" color="secondary"
                onClick={() => setTokenResult(null)}
              >
                Generate New
              </Button>
            </Box>
            <Typography variant="caption" color="text.secondary">
              Expires: {new Date(tokenResult.expires_at).toLocaleString()} ·{" "}
              Steps: {tokenResult.pending_steps.join(", ")}
            </Typography>
          </Box>
        ) : (
          <Box>
            <Button
              variant="contained"
              startIcon={<SendIcon />}
              onClick={() => tokenMut.mutate()}
              disabled={tokenMut.isPending}
            >
              {tokenMut.isPending ? "Generating…" : "Generate Onboarding Link"}
            </Button>
            {tokenMut.isError && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {String((tokenMut.error as Error)?.message)}
              </Alert>
            )}
          </Box>
        )}
      </Paper>

      {/* I-9 section 2 */}
      <Paper variant="outlined" sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2 }}>
        <Box>
          <Typography variant="subtitle1" fontWeight={600}>I-9 Verification — Section 2 (Employer)</Typography>
          <Typography variant="caption" color="text.secondary">
            Record completion of in-person document inspection (Form I-9 Section 2). For E-Verify / Tracker
            integrations, Section 2 is completed via webhook automatically.
          </Typography>
        </Box>

        {i9Result ? (
          <Alert severity="success" icon={<AssignmentTurnedInIcon />}>
            I-9 Section 2 completed on {new Date(i9Result.section2_completed_at!).toLocaleDateString()}.
            Status: <strong>{i9Result.status}</strong>
          </Alert>
        ) : (
          <>
            <TextField
              label="Scanned I-9 URL (optional)" size="small"
              value={i9FileUrl}
              onChange={(e) => setI9FileUrl(e.target.value)}
              helperText="URL of uploaded scanned I-9 PDF if stored in document management"
            />
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <Button
                variant="outlined"
                startIcon={<AssignmentTurnedInIcon />}
                onClick={() => i9Mut.mutate()}
                disabled={i9Mut.isPending}
              >
                {i9Mut.isPending ? "Saving…" : "Mark Section 2 Complete"}
              </Button>
              <Typography variant="caption" color="text.secondary">
                Records your identity (admin user) + IP address as the inspecting employer representative
              </Typography>
            </Box>
            {i9Mut.isError && (
              <Alert severity="error">
                {String((i9Mut.error as Error)?.message)}
              </Alert>
            )}
          </>
        )}
      </Paper>

      {/* Compliance checklist */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
          New Hire Compliance Checklist
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Item</TableCell>
              <TableCell>Method</TableCell>
              <TableCell align="center">Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {[
              { item: "Federal W-4", method: "Self-service or admin entry", done: false },
              { item: "State W-4", method: "Self-service or admin entry", done: false },
              { item: "Direct Deposit Setup", method: "Self-service (Plaid) or admin entry", done: false },
              { item: "I-9 Section 1 (employee)", method: "Self-service portal", done: false },
              { item: "I-9 Section 2 (employer)", method: "In-person inspection", done: i9Result?.status === "completed" },
              { item: "Offer Letter", method: "E-sign (coming soon)", done: false },
            ].map(row => (
              <TableRow key={row.item}>
                <TableCell>{row.item}</TableCell>
                <TableCell><Typography variant="caption" color="text.secondary">{row.method}</Typography></TableCell>
                <TableCell align="center">
                  <Chip
                    label={row.done ? "Complete" : "Pending"}
                    color={row.done ? "success" : "default"}
                    size="small"
                    variant={row.done ? "filled" : "outlined"}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Snackbar
        open={copySnack}
        autoHideDuration={2000}
        onClose={() => setCopySnack(false)}
        message="Link copied to clipboard"
      />
    </Box>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

function EmployeeDetailPage() {
  const { employeeId } = Route.useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const { data: employee, isLoading } = useQuery(payrollEmployeeQueryOptions(employeeId));

  if (isLoading) return (
    <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
      <CircularProgress />
    </Box>
  );
  if (!employee) return (
    <Box sx={{ p: 3 }}>
      <Alert severity="error">Employee not found</Alert>
    </Box>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate({ to: "/payroll/employees" })}
        sx={{ mb: 2 }}
      >
        Employees
      </Button>

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Typography variant="h5" fontWeight={600}>
            {employee.first_name} {employee.last_name}
          </Typography>
          <Chip
            label={employee.is_active ? "Active" : "Terminated"}
            color={employee.is_active ? "success" : "default"}
            size="small"
          />
        </Box>
        <HelpHint docPath="payroll-l1/employee-setup.md" label="Employee setup help" />
      </Box>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ mb: 3, borderBottom: 1, borderColor: "divider" }}
      >
        <Tab label="Profile" />
        <Tab label="W-4 & Tax" />
        <Tab label="Bank Accounts" />
        <Tab label="Onboarding & I-9" />
      </Tabs>

      {tab === 0 && <ProfileTab employee={employee} />}
      {tab === 1 && <W4TaxTab employee={employee} />}
      {tab === 2 && <BankAccountsTab employee={employee} />}
      {tab === 3 && <OnboardingTab employee={employee} />}
    </Box>
  );
}
